/**
 * AAAprinters - Authentication React Hook
 * Provides easy access to authentication state and methods
 */

import { useState, useEffect, useCallback } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import {
  signUp,
  signIn,
  signOut,
  getCurrentUser,
  getCurrentSession,
  onAuthStateChange,
  resetPassword,
  updatePassword
} from '@/lib/supabase-auth';
import type { SignUpData, SignInData } from '@/types/supabase-auth';

interface UseAuthReturn {
  user: User | null;
  session: Session | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  signUp: (data: SignUpData) => Promise<void>;
  signIn: (data: SignInData) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  clearError: () => void;
}

/**
 * Hook for managing authentication state
 */
export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize auth state
  useEffect(() => {
    let isMounted = true;

    async function initAuth() {
      try {
        const [currentUser, currentSession] = await Promise.all([
          getCurrentUser(),
          getCurrentSession()
        ]);

        if (isMounted) {
          setUser(currentUser);
          setSession(currentSession);
        }
      } catch (err) {
        console.error('Error initializing auth:', err);
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to initialize auth');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    initAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  // Listen to auth state changes
  useEffect(() => {
    const subscription = onAuthStateChange((user, session) => {
      setUser(user);
      setSession(session);
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Sign up handler
  const handleSignUp = useCallback(async (data: SignUpData) => {
    setLoading(true);
    setError(null);

    try {
      const result = await signUp(data);
      setUser(result.user);
      setSession(result.session);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sign up';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Sign in handler
  const handleSignIn = useCallback(async (data: SignInData) => {
    setLoading(true);
    setError(null);

    try {
      const result = await signIn(data);
      setUser(result.user);
      setSession(result.session);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sign in';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Sign out handler
  const handleSignOut = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      await signOut();
      setUser(null);
      setSession(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sign out';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset password handler
  const handleResetPassword = useCallback(async (email: string) => {
    setLoading(true);
    setError(null);

    try {
      await resetPassword({ email });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reset password';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update password handler
  const handleUpdatePassword = useCallback(async (password: string) => {
    setLoading(true);
    setError(null);

    try {
      await updatePassword({ password });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update password';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    user,
    session,
    loading,
    error,
    isAuthenticated: !!user,
    signUp: handleSignUp,
    signIn: handleSignIn,
    signOut: handleSignOut,
    resetPassword: handleResetPassword,
    updatePassword: handleUpdatePassword,
    clearError
  };
}
